import { useT } from "../../../shared/i18n/i18n";
import { useMemo, useState } from "react";
import { TablerIconGlyph } from "./tabler-icon-glyph";
import { normalizeTablerIconName } from "./tabler-icon-core";
import {
  TABLER_ICON_CATEGORIES,
  type TablerIconPickerContext,
  getRecommendedTablerIcons,
  getTablerIconsByCategory,
  getTablerIconCategoryLabel,
  getTablerIconLabel,
  searchTablerIcons,
} from "./tabler-icon-registry";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverHeader, PopoverTitle, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { Check, ChevronDown, Search, X } from "lucide-react";

type TablerIconPickerProps = {
  compact?: boolean;
  context?: TablerIconPickerContext;
  disabled?: boolean;
  onValueChange: (value: string) => void;
  value: string;
};

export function TablerIconPicker({
  compact = false,
  context = "default",
  disabled,
  onValueChange,
  value,
}: TablerIconPickerProps) {
  const t = useT();
  const [activeCategory, setActiveCategory] = useState("suggested");
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const selectedIcon = normalizeTablerIconName(value);
  const selectedLabel = getTablerIconLabel(selectedIcon);
  const hasQuery = query.trim().length > 0;
  const recommendedIcons = useMemo(() => getRecommendedTablerIcons(context), [context]);
  const category = activeCategory === "suggested" ? null : activeCategory;
  const visibleIcons = useMemo(() => {
    if (hasQuery) {
      return searchTablerIcons(query, { category, context, limit: 160 });
    }

    if (category) {
      return getTablerIconsByCategory(category, 160);
    }

    return recommendedIcons;
  }, [category, context, hasQuery, query, recommendedIcons]);

  function selectIcon(icon: string) {
    onValueChange(normalizeTablerIconName(icon));
    setOpen(false);
    setQuery("");
    setActiveCategory("suggested");
  }

  return (
    <Popover
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) {
          setQuery("");
          setActiveCategory("suggested");
        }
      }}
      open={open}
    >
      <PopoverTrigger
        render={
          <Button
            aria-label={t("iconPicker.choose")}
            className={cn(
              "border-input bg-input/20 font-normal text-foreground hover:bg-input/30 dark:bg-input/30",
              compact ? "relative size-10 p-0" : "w-full justify-between",
            )}
            disabled={disabled}
            size={compact ? "icon-lg" : "default"}
            type="button"
            variant="outline"
          />
        }
      >
        {compact ? (
          <TablerIconGlyph className="size-5 text-muted-foreground" icon={selectedIcon} />
        ) : (
          <>
            <span className="flex min-w-0 items-center gap-2">
              <span className="grid size-5 shrink-0 place-items-center rounded-sm bg-muted/45 text-muted-foreground">
                <TablerIconGlyph className="size-3.5" icon={selectedIcon} />
              </span>
              <span className="truncate">{selectedLabel}</span>
            </span>
            <ChevronDown data-icon="inline-end" />
          </>
        )}
        {compact ? (
          <span className="pointer-events-none absolute -bottom-0.5 -right-0.5 grid size-4 place-items-center rounded-sm bg-background text-muted-foreground ring-1 ring-border">
            <ChevronDown className="size-3" />
          </span>
        ) : null}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[min(18rem,calc(100vw-2rem))] overflow-hidden p-0">
        <PopoverHeader className="gap-1 p-2 pb-1">
          <PopoverTitle className="sr-only">{t("iconPicker.library")}</PopoverTitle>
          <label className="relative block">
            <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="h-7 pl-7 pr-7"
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("iconPicker.search")}
              value={query}
            />
            {hasQuery ? (
              <Button
                aria-label={t("iconPicker.clearSearch")}
                className="absolute right-1 top-1/2 size-5 -translate-y-1/2 p-0"
                onClick={() => setQuery("")}
                size="icon-xs"
                type="button"
                variant="ghost"
              >
                <X className="size-3" />
              </Button>
            ) : null}
          </label>
          <Tabs onValueChange={setActiveCategory} value={activeCategory}>
            <TabsList
              className="h-7 w-full justify-start overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              variant="line"
            >
              <TabsTrigger className="flex-none" value="suggested">
                {t("iconPicker.suggested")}
              </TabsTrigger>
              {TABLER_ICON_CATEGORIES.map((item) => (
                <TabsTrigger className="flex-none" key={item} value={item}>
                  {getTablerIconCategoryLabel(item)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </PopoverHeader>

        <div className="px-2 pb-2">
          <IconSection
            emptyLabel={t("board.noMatchingIcons")}
            icons={visibleIcons}
            onSelect={selectIcon}
            selectedIcon={selectedIcon}
            title={hasQuery ? "Search results" : null}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}

function IconSection({
  emptyLabel,
  icons,
  onSelect,
  selectedIcon,
  title,
}: {
  emptyLabel?: string;
  icons: string[];
  onSelect: (icon: string) => void;
  selectedIcon: string;
  title: string | null;
}) {
  return (
    <div className="grid gap-1">
      {title ? <p className="px-1 text-[0.6875rem] font-medium text-muted-foreground">{title}</p> : null}
      {icons.length > 0 ? (
        <div className="grid max-h-52 grid-cols-7 gap-1.5 overflow-y-auto">
          {icons.map((icon) => {
            const checked = icon === selectedIcon;
            return (
              <button
                aria-label={getTablerIconLabel(icon)}
                aria-pressed={checked}
                className={cn(
                  "group relative grid size-8 place-items-center rounded-md border border-transparent text-muted-foreground transition-colors hover:bg-muted/45 hover:text-foreground focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/30 focus-visible:outline-none",
                  checked && "border-primary/45 bg-primary/10 text-foreground",
                )}
                key={icon}
                onClick={() => onSelect(icon)}
                title={getTablerIconLabel(icon)}
                type="button"
              >
                <TablerIconGlyph className="size-4.5" icon={icon} />
                {checked ? <Check className="absolute right-1 top-1 size-3 text-primary" /> : null}
              </button>
            );
          })}
        </div>
      ) : (
        <div className="grid h-20 place-items-center rounded-md border border-dashed border-border/70 text-xs text-muted-foreground">
          {emptyLabel}
        </div>
      )}
    </div>
  );
}
